import React, { useCallback, useState } from "react";
import plusFill from "@iconify/icons-eva/plus-fill";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import copyFill from "@iconify/icons-eva/copy-fill";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { Icon } from "@iconify/react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";
import * as locales from "@material-ui/core/locale";
import Scrollbar from "../../Scrollbar";
import { TablaHead } from "../../_dashboard/tablas";
import { filter } from "lodash";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "src/components/snackbar";

import AgregarClavesPortalesClientes from "./AgregarClavesPortalesClientes";
import { clientesApi } from "src/services/clientesApi";
import { Search as SearchIcon } from "@material-ui/icons";

const styles = makeStyles((theme) => ({
  root: {
    minWidth: 275,
  },
  title: {
    textAlign: "center",
    fontSize: "24px",
    marginBottom: "1rem",
  },
  textfield: {
    width: "100%",
  },
  grid: {
    xs: 12,
    alignItems: "center",
    justify: "center",
    marginBottom: "1rem",
  },
  pos: {
    marginBottom: 12,
  },
  formControl: {
    minWidth: "100%",
    marginBottom: 12,
    marginTop: 12,
  },
}));

const TABLE_HEAD = [
  { id: "usuario", label: "Nombre Usuario", alignRight: false },
  { id: "clave", label: "Contraseña", alignRight: false },
  { id: "institucion", label: "Institución", alignRight: false },
  { id: "tipoClave", label: "Tipo de Clave", alignRight: false },
  { id: "acciones", label: "Acciones", alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.nombre.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

function generarClaveUnica(refCliente, institucion, usuario) {
  return `${refCliente}-${institucion}-${usuario}`;
}

export default function EditarClavesClienteSeleccionado(props) {
  const { value, cliente, setShowAlert } = props;
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("nombre");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locale] = useState("esES");
  const [busqueda, setBusqueda] = useState("");
  const [passwordsVisibles, setPasswordsVisibles] = useState({});
  const [passwordsDesencriptadas, setPasswordsDesencriptadas] = useState({});
  const [claveDesencriptando, setClaveDesencriptando] = useState(null);

  const classes = styles();

  const { enqueueSnackbar } = useSnackbar();

  const { data: clavesPortales = [], isLoading: isLoadingClaves } =
    clientesApi.useObtenerClavesClienteQuery(
      { rut: cliente.rut },
      { skip: !cliente.rut },
    );

  const [desencriptarClave] = clientesApi.useDesencriptarClaveMutation();

  const handleTogglePassword = useCallback(
    async (refCliente, institucion, usuario) => {
      const claveUnica = generarClaveUnica(refCliente, institucion, usuario);

      // Si ya está visible, solo ocultarla
      if (passwordsVisibles[claveUnica]) {
        setPasswordsVisibles((prev) => ({
          ...prev,
          [claveUnica]: false,
        }));
        return;
      }

      // Establecer esta clave como la que se está desencriptando
      setClaveDesencriptando(claveUnica);

      // Si no está visible, desencriptar desde el backend
      try {
        const resultado = await desencriptarClave({
          refCliente,
          institucion,
          usuario,
          refSucursal: "CL",
          accion: "VISUALIZACION",
        }).unwrap();

        // Guardar la contraseña desencriptada
        setPasswordsDesencriptadas((prev) => ({
          ...prev,
          [claveUnica]: resultado.claveDesencriptada,
        }));

        // Marcar como visible
        setPasswordsVisibles((prev) => ({
          ...prev,
          [claveUnica]: true,
        }));

        // Mostrar advertencia si necesita migración
        if (resultado.necesitaMigracion) {
          enqueueSnackbar(
            "Esta clave está en formato antiguo. Se recomienda actualizarla.",
            {
              variant: "info",
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
              },
            },
          );
        }
      } catch (error) {
        console.error("Error al desencriptar:", error);

        let mensaje = "Error al obtener la contraseña";
        if (error.status === 403) {
          mensaje = "No tienes permisos para ver esta contraseña";
        } else if (error.status === 404) {
          mensaje = "Contraseña no encontrada";
        }

        enqueueSnackbar(mensaje, {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      } finally {
        setClaveDesencriptando(null);
      }
    },
    [passwordsVisibles, desencriptarClave, enqueueSnackbar],
  );

  const handleCopyPassword = useCallback(
    async (refCliente, institucion, usuario, claveEncriptada) => {
      const claveUnica = generarClaveUnica(refCliente, institucion, usuario);

      try {
        let passwordToCopy;

        // Si ya la tenemos desencriptada, usarla
        if (passwordsDesencriptadas[claveUnica]) {
          passwordToCopy = passwordsDesencriptadas[claveUnica];
        } else {
          // Establecer esta clave como la que se está desencriptando
          setClaveDesencriptando(claveUnica);

          // Si no, desencriptar primero
          const resultado = await desencriptarClave({
            refCliente,
            institucion,
            usuario,
            refSucursal: "CL",
            accion: "COPIA",
          }).unwrap();

          passwordToCopy = resultado.claveDesencriptada;

          // Guardarla para uso futuro
          setPasswordsDesencriptadas((prev) => ({
            ...prev,
            [claveUnica]: resultado.claveDesencriptada,
          }));

          // Limpiar loading después de desencriptar
          setClaveDesencriptando(null);
        }

        // Copiar al portapapeles
        await navigator.clipboard.writeText(passwordToCopy);

        enqueueSnackbar("Contraseña copiada (se limpiará en 45 segundos)", {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });

        // Limpiar portapapeles después de 45 segundos
        setTimeout(async () => {
          try {
            const current = await navigator.clipboard.readText();
            if (current === passwordToCopy) {
              await navigator.clipboard.writeText("");
              console.log("Portapapeles limpiado por seguridad");
            }
          } catch (error) {
            // Ignorar errores
            console.log("No se pudo limpiar portapapeles:", error.message);
          }
        }, 45000);
      } catch (error) {
        console.error("Error al copiar:", error);

        setClaveDesencriptando(null);

        enqueueSnackbar("Error al copiar la contraseña", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
      }
    },
    [passwordsDesencriptadas, desencriptarClave, enqueueSnackbar],
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = clavesPortales.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - clavesPortales.length)
      : 0;

  const clavesFiltradas = useCallback(() => {
    if (busqueda.trim() === "") {
      return clavesPortales;
    }

    const busquedaLower = busqueda.toLowerCase();
    return clavesPortales.filter(
      (clave) =>
        clave.institucion?.toLowerCase().includes(busquedaLower) ||
        clave.usuario?.toLowerCase().includes(busquedaLower),
    );
  }, [clavesPortales, busqueda]);

  const filteredUsers = applySortFilter(
    clavesFiltradas(),
    getComparator(order, orderBy),
    "",
  );

  const isUserNotFound = filteredUsers.length === 0;

  return (
    <div className={classes.pos}>
      {value === 0 && (
        <Card className={classes.root} variant="outlined">
          <CardContent>
            <Grid container className={classes.grid} spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  component="h2"
                  className={classes.title}
                >
                  Claves Portales
                </Typography>
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por institución o usuario..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} align="end">
                <Button
                  onClick={() => {
                    setOpen(true);
                  }}
                  variant="contained"
                  startIcon={<Icon icon={plusFill} />}
                  fullWidth
                >
                  Agregar Claves
                </Button>
              </Grid>
            </Grid>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <TablaHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={clavesPortales.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredUsers
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => {
                        const {
                          id,
                          usuario,
                          clave,
                          institucion,
                          tipoClave,
                          refCliente,
                        } = row;
                        const isItemSelected = selected.indexOf(usuario) !== -1;

                        // ✅ Generar clave única para esta fila
                        const claveUnica = generarClaveUnica(
                          refCliente || cliente.rut,
                          institucion,
                          usuario,
                        );
                        const estaVisible =
                          passwordsVisibles[claveUnica] || false;
                        const passwordDesencriptada =
                          passwordsDesencriptadas[claveUnica];

                        return (
                          <TableRow
                            hover
                            key={id || `${usuario}-${institucion}`}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            {/* Columna: Usuario */}
                            <TableCell align="left">{usuario}</TableCell>

                            {/* Columna: Contraseña */}
                            <TableCell align="left">
                              <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Grid item>
                                  {estaVisible
                                    ? passwordDesencriptada
                                    : "••••••••••••"}
                                </Grid>
                                <Grid item>
                                  <Tooltip
                                    title={
                                      estaVisible
                                        ? "Ocultar contraseña"
                                        : "Ver contraseña"
                                    }
                                  >
                                    <IconButton
                                      onClick={() =>
                                        handleTogglePassword(
                                          refCliente || cliente.rut,
                                          institucion,
                                          usuario,
                                        )
                                      }
                                      edge="end"
                                      size="small"
                                    >
                                      {claveDesencriptando === claveUnica ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <Icon
                                          icon={
                                            estaVisible ? eyeOffFill : eyeFill
                                          }
                                        />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </TableCell>

                            {/* Columna: Institución */}
                            <TableCell align="left">{institucion}</TableCell>

                            {/* Columna: Tipo de Clave */}
                            <TableCell align="left">
                              {tipoClave === undefined || tipoClave === null
                                ? "---"
                                : tipoClave}
                            </TableCell>

                            {/* Columna: Acciones */}
                            <TableCell align="left">
                              <Tooltip title="Copiar contraseña">
                                <IconButton
                                  onClick={() =>
                                    handleCopyPassword(
                                      refCliente || cliente.rut,
                                      institucion,
                                      usuario,
                                      clave,
                                    )
                                  }
                                  size="small"
                                  color="primary"
                                >
                                  {claveDesencriptando === claveUnica ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <Icon icon={copyFill} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={5} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          {isLoadingClaves ? (
                            <CircularProgress size={24} />
                          ) : clavesPortales.length === 0 ? (
                            "No tienes claves registradas aún. Agrega tu primera clave."
                          ) : (
                            "No se encontraron claves con ese criterio de búsqueda."
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <ThemeProvider
              theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={clavesPortales.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </ThemeProvider>

            {open && (
              <AgregarClavesPortalesClientes
                open={open}
                setOpen={setOpen}
                refCliente={cliente.rut}
                setShowAlert={setShowAlert}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
